import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { QuestionnaireService } from '@shared/services/questionnaire.service';
import { GridRefreshService } from '@shared/services/grid-refresh.service';
import { CrudOperationsService } from '@app/shared/components/cell-renderers/crud-operations-renderer/crud-operations.service';

import { QuestionnaireDialogComponent } from './questionnaire-dialog/questionnaire-dialog.component';

import { DataGridConfig } from '@app/shared/components/data-grid/data-grid-config';

import {
  CrudOperationsRendererComponent
} from '@shared/components/cell-renderers/crud-operations-renderer/crud-operations-renderer.component';

import { AnswerSetDialogComponent } from '@app/shared/components/dialogs/answer-set-dialog/answer-set-dialog.component';

@Component({
  selector: 'db-questionnaires',
  templateUrl: './questionnaires.component.html',
  styleUrls: ['./questionnaires.component.scss']
})
export class QuestionnairesComponent implements OnInit, OnDestroy {
  dataGridConfig: DataGridConfig;
  rowData: any[];

  questionnaireDialogRef: MatDialogRef<QuestionnaireDialogComponent>;
  answerSetDialogRef: MatDialogRef<AnswerSetDialogComponent>;
  subscriptionManager = new Subscription();

  constructor(
    private dialog: MatDialog,
    private questionnaireService: QuestionnaireService,
    private gridRefreshService: GridRefreshService,
    private crudOperationsService: CrudOperationsService,
    private router: Router
  ) {

    const gridRefreshSubscription = this.gridRefreshService.gridRefresh$
      .subscribe(() => {
        this.initializeRowData();
      });

    const editOperationSubscription = this.crudOperationsService.editOperation$
      .subscribe(params => {
        this.editOperation(params);
      });

    const cloneOperationSubscription = this.crudOperationsService.cloneOperation$
      .subscribe(params => {
        this.cloneOperation(params);
      });

    const answerSetOperation = this.crudOperationsService.answerSetOperation$
      .subscribe(params => {
        this.answerSetOperation(params);
      });

    this.subscriptionManager
      .add(gridRefreshSubscription)
      .add(editOperationSubscription)
      .add(cloneOperationSubscription)
      .add(answerSetOperation);
  }

  ngOnInit() {
    this.initializeRowData();

    this.dataGridConfig = {
      columnDefs: [
        {
          headerName: 'Name',
          field: 'questionnaireName',
          minWidth: 200
        },
        {
          headerName: 'Date Created',
          field: 'dateCreated',
          minWidth: 150
        },
        {
          headerName: 'Date Modified',
          field: 'dateModified',
          minWidth: 150
        },
        {
          headerName: 'Created By',
          field: 'createdBy',
          minWidth: 150
        },
        {
          headerName: 'Modified By',
          field: 'modifiedBy',
          minWidth: 150
        },
        {
          headerName: '',
          field: 'id',
          cellRenderer: 'crudOperationsRenderer',
          cellRendererParams: {
            link: '/questionnaires/questionnaire',
            operations: {
              edit: {
                label: 'Edit'
              },
              clone: {
                label: 'Clone'
              },
              delete: {
                label: 'Delete'
              },
              answerSet: {
                label: 'New Answer Set'
              }
            }
          },
          minWidth: 300,
          suppressSorting: true,
          suppressFilter: true
        }
      ],
      frameworkComponents: {
        crudOperationsRenderer: CrudOperationsRendererComponent
      }
    };
  }

  editOperation(params) {
    this.router.navigate([params.link, params.value]);
  }

  cloneOperation(params) {
    this.questionnaireDialogRef = this.dialog.open(QuestionnaireDialogComponent, {
      width: '500px',
      panelClass: 'db-dialog',
      data: {
        params: params,
        questionnaireId: params.data.id,
        questionnaireName: params.data.questionnaireName,
        questionnaireSector: params.data.questionnaireSector,
        dialogTitle: 'Clone Questionnaire',
        submitButtonLabel: 'Clone Questionnaire'
      }
    });

    this.questionnaireDialogRef.afterClosed().pipe(
      filter(question => question)
    ).subscribe(questionnaireDialog => {

      const qd = JSON.parse(questionnaireDialog);

      this.questionnaireService.cloneQuestionnaires(qd.questionnaireName, qd.questionnaireId).subscribe(
        () => {
          this.gridRefreshService.refreshGridData();
        },
        error => {
          console.log(error);
        });
    });

    return false;
  }

  answerSetOperation(params) {
    this.answerSetDialogRef = this.dialog.open(AnswerSetDialogComponent, {
      width: '500px',
      panelClass: 'db-dialog',
      data: {
        params: params,
        dialogTitle: 'New Answer Set',
        submitButtonLabel: 'Add Answer Set',
        submitButtonTypeClass: 'button--accent'
      }
    });

    this.answerSetDialogRef.afterClosed()
      .pipe(filter(question => question))
      .subscribe(([answerSetFormValue, clientId]) => {
        if (!answerSetFormValue) {
          return;
        }

        const answerSetFormData = JSON.parse(answerSetFormValue);

        this.questionnaireService
          .addAnswerSet(answerSetFormData, clientId)
          .subscribe(() => {
        });
    });
  }

  initializeRowData() {
    this.questionnaireService.getQuestionnaires()
      .subscribe(data => {
        this.rowData = data;
      });
  }

  openQuestionnaireDialog() {
    this.questionnaireDialogRef = this.dialog.open(QuestionnaireDialogComponent, {
      width: '500px',
      panelClass: 'db-dialog',
      data: {
        dialogTitle: 'Add Questionnaire',
        submitButtonLabel: 'Add Questionnaire'
      }
    });

    this.questionnaireDialogRef.afterClosed().pipe(
      filter(question => question)
    ).subscribe(questionnaireDialog => {
      const qd = JSON.parse(questionnaireDialog);

      this.questionnaireService
        .addQuestionnaires(qd.questionnaireName)
        .subscribe(() => {
          this.questionnaireService
            .getQuestionnaires()
            .subscribe(data => {
              this.rowData = data;
            },
              error => {
                console.log(error);
              });
        },
          error => {
            console.log(error);
          });
    });

    return false;
  }

  ngOnDestroy() {
    this.subscriptionManager.unsubscribe();
  }
}
